import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatUSD } from "@/components/KPICard";
import { TrendingUp, TrendingDown, Info } from "lucide-react";

/**
 * Portfolio-wide annual yield.
 *
 * Value-weighted average of each property's current-contract-year net yield.
 * Uses the same formula as /property_yield/{id}, aggregated across all
 * properties. Properties without an active contract drag the yield down
 * (they add to value_usd but not to adjusted_income_usd).
 */
export default function PortfolioYieldCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/portfolio_yield");
        setData(r.data);
      } catch (_) { /* handled by axios */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-5 animate-pulse text-sm text-muted-foreground">
        Calculando rendimiento global…
      </div>
    );
  }
  if (!data || data.total_value_usd === 0) return null;

  const positive = data.net_yield_pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const yieldColor = positive ? "text-emerald-500" : "text-rose-500";

  return (
    <div
      className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-card to-card p-5 sm:p-6 space-y-4 animate-fade-in-up"
      data-testid="portfolio-yield-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Rendimiento anual del portafolio (Neto)
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Promedio ponderado por valor · año contractual en curso de cada propiedad
          </div>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          {data.properties_with_contract} de {data.properties_total} propiedades con contrato
          {data.properties_without_contract > 0 && (
            <div className="text-amber-500">
              {data.properties_without_contract} vacantes arrastran el yield ↓
            </div>
          )}
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <Icon className={`w-8 h-8 ${yieldColor}`} />
        <div className={`text-5xl sm:text-6xl font-heading font-bold num ${yieldColor}`} data-testid="portfolio-yield-pct">
          {data.net_yield_pct.toFixed(2)}%
        </div>
        <div className="text-xs text-muted-foreground pb-2 num">
          bruto: {data.gross_yield_pct.toFixed(2)}%
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-border/40">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ingresos ajustados</div>
          <div className="text-sm font-heading font-semibold text-emerald-500 num">{formatUSD(data.adjusted_income_usd)}</div>
          <div className="text-[10px] text-muted-foreground num">cobrado: {formatUSD(data.collected_income_usd)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gastos reales</div>
          <div className="text-sm font-heading font-semibold text-rose-500 num">{formatUSD(data.real_expenses_usd)}</div>
          <div className="text-[10px] text-muted-foreground">solo tx registradas</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Neto USD</div>
          <div className={`text-sm font-heading font-semibold num ${positive ? "text-emerald-500" : "text-rose-500"}`}>{formatUSD(data.net_usd)}</div>
          <div className="text-[10px] text-muted-foreground">ingresos − gastos</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valor total</div>
          <div className="text-sm font-heading font-semibold num">{formatUSD(data.total_value_usd)}</div>
          <div className="text-[10px] text-muted-foreground">base del %</div>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground border-t border-border/40 pt-3 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Al inicio (sin cobros ni gastos), Neto = Bruto proyectado. A medida que registrás cobros reales
          y gastos, el % se recalcula automáticamente en tiempo real.
        </div>
      </div>
    </div>
  );
}
